import { EProjectPermission } from "../types/permission";

// NOTE: Lowercased comparison keys for the two default role names, shared
// across clients. The DB stores them titleCased ("Admin" / "Collaborator"),
// so always compare via name.trim().toLowerCase() against these.
export const ADMIN_ROLE_NAME = "admin";
export const COLLABORATOR_ROLE_NAME = "collaborator";

export const ACTION_COLUMNS = ["read", "write", "delete"] as const;

export type TActionType = (typeof ACTION_COLUMNS)[number];

// NOTE: Tailwind class names for the read/write/delete badges in the role-config UI.
// Web-only consumer — mobile renders these badges via theme-color inline styles
// in PermissionBadge.tsx (it can't consume Tailwind class strings the same way).
export const BADGE_CLASS_NAME_MAP: Record<TActionType, string> = {
  read: "bg-permission-read-background text-permission-read-foreground hover:bg-permission-read-background",
  write:
    "bg-permission-write-background text-permission-write-foreground hover:bg-permission-write-background",
  delete:
    "bg-permission-delete-background text-permission-delete-foreground hover:bg-permission-delete-background",
} as const;

export const ACTION_DEPENDENCIES: Record<TActionType, TActionType[]> = {
  read: [],
  write: ["read"],
  delete: ["read", "write"],
};

export const ACTION_DEPENDENTS: Record<TActionType, TActionType[]> = {
  read: ["write", "delete"],
  write: ["delete"],
  delete: [],
};

// NOTE: Default permissions that are always enabled for a new role.
// INTEGRATION_READ is included so new roles can at minimum see configured
// integrations in the project — matches the historical web default.
export const DEFAULT_ENABLED_PERMISSIONS = [
  EProjectPermission.PROJECT_READ,
  EProjectPermission.CHAT_SETTINGS_READ,
  EProjectPermission.INTEGRATION_READ,
];

// NOTE: Cross-group dependencies where selecting any action in the key group
// requires the corresponding action in the value group.
// Mirrors PERMISSION_REQUIRES on the server — kept in sync manually because the
// server map uses fully-qualified enum members while these maps work at the
// group level (the role-config UI groups permissions by entity).
//
// public_assistant is deliberately NOT linked to private_assistant: under the
// per-type model the two are independent. Sharing requires private write because
// you must be able to edit before deciding who else can.
export const GROUP_DEPENDENCIES: Record<string, string> = {
  automation_manager: "automation",
  private_assistant_share: "private_assistant",
  // NOTE: NSAI-5657 — pinning a dashboard for the team is done by someone who
  // can edit it, so dashboard_manager:write needs dashboard:write.
  dashboard_manager: "dashboard",
};

// NOTE: Reverse of GROUP_DEPENDENCIES. Deselecting an action in the key group
// should also deselect the same action in the value group.
export const GROUP_DEPENDENTS: Record<string, string> = {
  automation: "automation_manager",
  private_assistant: "private_assistant_share",
  dashboard: "dashboard_manager",
};
